// Standard library imports
import React, { useState, useEffect, useContext } from 'react';

// External library imports
import { UilImport, UilInfoCircle, UilLightbulbAlt, UilFileLandscapeAlt } from '@iconscout/react-unicons';
import moment from 'moment';

// Internal module imports
import Table from '../../../components/Table/Table';
import { DownloadAsExcel } from '../../../utils/downloadAsExcel';
import Typography from '../../../components/Typography/Typography';
import { momentTimeFormater } from '../../../utils/dateHelper';
import { DropdownComponent } from '../../../components/Inputs/Input';
import { START_LOADER, STOP_LOADER } from '../../../constants';
import { LoaderContext } from '../../../context/LoaderContext';
import DateRangePicker from '../../../components/Inputs/DateRangePicker/DateRangePicker';
import { truncateNumber } from '../../../utils/numberHelpers';
import CustomTooltip from '../../../components/CustomToolTip/CustomTooltip';
import buttonClasses from '../../../components/Buttons/Button.module.css';
import ModalComponent from '../../../components/ModalComponent/ModalComponent';
import SessionInfoModal from './SessionInfoModel';
import _ from 'lodash';
import SessionLogModal from './SessionLogModal';

// Css module imports
import myClasses from '../SmartCharging.module.css';
import classes from '../../../styles/AllDevices.module.css';
import { SCService } from '../../../services/SCService';
import SmartChargeInfoModal from './SmartChargeInfoModal';

const LIMIT = 15;
const today = new Date();
const yesterday = new Date(today);
yesterday.setDate(yesterday.getDate() - 1);

const statusOptions = [
    { label: 'ACTIVE', value: 'ACTIVE' },
    { label: 'COMPLETED', value: 'COMPLETED' },
];

const Sessions = ({ instances, updateQueryParams, queryParamsData, sites = [] }) => {
    const { dispatch: loaderDispatch } = useContext(LoaderContext);
    const startLoader = (payload) => loaderDispatch({ type: START_LOADER, payload });
    const stopLoader = (payload) => loaderDispatch({ type: STOP_LOADER, payload });

    const [tableData, setTableData] = useState([]);
    const [totalConsumption, setTotalConsumption] = useState([]);
    const [totalSavings, setSavings] = useState(0);
    const [skip, setSkip] = useState(0);
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [dateRange, setDateRange] = useState({
        startDate: queryParamsData.dateRange ? new Date(queryParamsData.dateRange.startDate) : yesterday,
        endDate: queryParamsData.dateRange ? new Date(queryParamsData.dateRange.endDate) : today,
    });

    const defaultValue = {
        label: 'All',
        value: 'All',
    };
    const [groups, setGroups] = useState([defaultValue]);
    const [selectedGroup, setSelectedGroup] = useState(queryParamsData.group ? queryParamsData.group : defaultValue);

    const [chargers, setChargers] = useState([defaultValue]);
    const [selectedCharger, setSelectedCharger] = useState(queryParamsData.evse ? queryParamsData.evse : defaultValue);
    const [connectors, setConnectors] = useState([defaultValue]);
    const [selectedConnector, setSelectedConnector] = useState({ ...defaultValue, key: defaultValue.value }); // adding key to handle "All" value change
    const [tags, setTags] = useState([defaultValue]);
    const [selectedTag, setSelectedTag] = useState(queryParamsData.tag ? queryParamsData.tag : defaultValue);
    const [selectedStatus, setSelectedStatus] = useState(queryParamsData.status ? queryParamsData.status : statusOptions[0]);
    const [infoModal, setInfoModal] = useState({ status: false, data: '' });
    const [scInfoModal, setSCInfoModal] = useState({ status: false, sessionId: '', evseId: '' });
    const [logsModal, setLogsModal] = useState({ status: false, sessionId: '' });

    useEffect(() => {
        if (instances?.length) {
            const processedData = [];
            instances.forEach((charger) => {
                if (selectedGroup.value === 'All' || selectedGroup.value === charger.meta?.groupId) {
                    processedData.push({
                        label: charger.name,
                        value: charger.meta?.evseId,
                        connectors: charger.meta?.connectors,
                        connectorDetails: charger.meta?.connectorDetails,
                        evseId: charger.meta?.evseId,
                    });
                }
            });

            setChargers([defaultValue, ...processedData]);
            setSelectedCharger(defaultValue);
        }
    }, [instances, selectedGroup.value]);

    useEffect(() => {
        const processedData = [];
        if (selectedCharger.value !== 'All') {
            for (let i = 0; i < selectedCharger?.connectorDetails.length; i++) {
                processedData.push({
                    label: selectedCharger?.connectorDetails[i]?.connectorId,
                    value: selectedCharger?.connectorDetails[i]?.connectorId,
                });
            }
        }
        setConnectors([defaultValue, ...processedData]);
        setSelectedConnector({ ...defaultValue, key: `All_${selectedCharger.value}` });
    }, [selectedCharger.value]);

    useEffect(() => {
        setGroups([defaultValue, ...sites]);
    }, [sites]);

    useEffect(() => {
        getAllTags();
    }, [selectedGroup.value]);

    useEffect(() => {
        fetchSessionData();
    }, [selectedConnector.key, selectedGroup.value, selectedTag.value, selectedStatus, skip]);

    useEffect(() => {
        updateQueryParams('dateRange', JSON.stringify(dateRange));
    }, [dateRange.startDate, dateRange.endDate]);

    useEffect(() => {
        updateQueryParams('evse', JSON.stringify(selectedCharger));
    }, [selectedCharger]);

    useEffect(() => {
        updateQueryParams('group', JSON.stringify(selectedGroup));
    }, [selectedGroup]);

    useEffect(() => {
        updateQueryParams('tag', JSON.stringify(selectedTag));
    }, [selectedTag]);

    useEffect(() => {
        updateQueryParams('status', JSON.stringify(selectedStatus));
    }, [selectedStatus]);

    const getAllTags = () => {
        SCService.GetAllTags(
            {
                skip: 0,
                limit: 10000,
                groupId: selectedGroup.value,
            },
            () => startLoader('getTags'),
            handleGetTagSuccess,
            handleError,
            () => stopLoader('getTags')
        );
    };

    const handleError = (error) => {
        console.log(error);
    };

    const handleGetTagSuccess = ({ data }) => {
        const { tags } = data?.data;
        const processedData = tags?.map((item) => {
            return {
                label: item.name,
                value: item.tagID,
            };
        });
        setTags([defaultValue, ...processedData]);
        setSelectedTag(defaultValue);
    };

    const fetchSessionData = (forExport = false) => {
        SCService.GetSessionsData(
            {
                evseId: selectedCharger?.evseId ? selectedCharger?.evseId : 'All',
                connectorId: selectedConnector.value,
                groupId: selectedGroup.value,
                tagId: selectedTag.value,
                status: selectedStatus.value,
                startDate: momentTimeFormater(moment(dateRange.startDate).format('YYYY-MM-DD')).startOf('day').toDate(),
                endDate: momentTimeFormater(moment(dateRange.endDate).format('YYYY-MM-DD')).endOf('day').toDate(),
                limit: forExport ? 100000 : LIMIT,
                skip: forExport ? 0 : skip,
            },
            startLoader,
            forExport ? handleExport : handleSessionDataSuccess,
            handleError,
            stopLoader
        );
    };

    const getSavings = (data) => {
        const savings = _.get(data, ['savingsInfo'], null);
        return (
            <div className={myClasses.ActionIcons}>
                <CustomTooltip
                    content={
                        savings ? (
                            <div>
                                <span>Smart Cost: {truncateNumber(savings.smartCost, 3)} kr.</span>
                                <br />
                                <span>Default Cost: {truncateNumber(savings.hypotheticalCost, 3)} kr.</span>
                                <br />
                                <span>Savings: {truncateNumber(savings.savings, 3)} kr.</span>
                                <br />
                                <span>Savings (%): {truncateNumber(savings.savingsPercent, 3)} %</span>
                            </div>
                        ) : (
                            'No savings data'
                        )
                    }
                >
                    {savings ? `${truncateNumber(savings.savings, 3)}*` : '--'}
                </CustomTooltip>
            </div>
        );
    };

    const getActions = (data) => {
        return (
            <div className={myClasses.ActionIcons}>
                <div onClick={() => setInfoModal({ status: true, data })}>
                    <CustomTooltip content={'Info'}>
                        <UilInfoCircle className={buttonClasses.ButtonIcon} />
                    </CustomTooltip>
                </div>
                <div
                    onClick={() =>
                        setSCInfoModal({
                            status: true,
                            sessionId: data.session_id,
                            evseId: data.evse_uid,
                        })
                    }
                >
                    <CustomTooltip content={'Charging Profiles'}>
                        <UilLightbulbAlt className={buttonClasses.ButtonIcon} />
                    </CustomTooltip>
                </div>
                <div
                    onClick={() =>
                        setLogsModal({
                            status: true,
                            sessionId: data.session_id,
                        })
                    }
                >
                    <CustomTooltip content={'Logs'}>
                        <UilFileLandscapeAlt className={buttonClasses.ButtonIcon} />
                    </CustomTooltip>
                </div>
            </div>
        );
    };

    const handleSessionDataSuccess = ({ data }) => {
        const { sessions, overallConsumption, totalCount, totalSavings } = data?.data[0];

        const processedData = sessions?.map((item, index) => {
            return {
                sessionId: item.session_id,
                tagId: item.cdr_token?.uid ? item.cdr_token?.uid : '--',
                tagName: item.tagName ? item.tagName : '--',
                evseId: item.evse_uid,
                connectorId: item.connector_id,
                consumption: item.kwh ? truncateNumber(item.kwh, 3) : 0,
                status: item.status,
                type: item.type ? item.type : '--',
                chargingProfileStatus: _.get(item, ['chargingProfiles', 0, 'evseStatus'])
                    ? _.get(item, ['chargingProfiles', 0, 'evseStatus'])
                    : _.get(item, ['chargingProfiles', 0])
                        ? 'SENT'
                        : 'NOT SENT',
                startTime: item.start_date_time ? momentTimeFormater(item.start_date_time).format('YYYY-MM-DD HH:mm:ss') : '--',
                endTime: item.end_date_time ? momentTimeFormater(item.end_date_time).format('YYYY-MM-DD HH:mm:ss') : '--',
                lastReportedSOC: item.latestSOC ? item.latestSOC : '--',
                savings: getSavings(item),
                action: getActions(item),
            };
        });

        setTableData(processedData);
        setTotalConsumption(overallConsumption?.length ? truncateNumber(overallConsumption[0].overallConsumption, 3) : 0);
        setSavings(totalSavings?.length ? truncateNumber(totalSavings[0].savings, 3) : 0);
        setTotalCount(_.get(_.head(totalCount), 'totalCount', 0));
    };

    const handleExport = ({ data }) => {
        const { sessions } = data?.data[0];
        const excelData = sessions.map((sdata) => {
            const savings = _.get(sdata, ['savingsInfo'], null);
            return {
                a: sdata.session_id,
                b: sdata.tagName ? sdata.tagName : '--',
                c: sdata.evse_uid,
                d: sdata.connector_id,
                e: sdata.kwh ? truncateNumber(sdata.kwh, 3) : 0,
                f: sdata.status,
                g: sdata.latestSOC ? sdata.latestSOC : '--',
                h: sdata.start_date_time ? momentTimeFormater(sdata.start_date_time).format('YYYY-MM-DD HH:mm:ss') : '--',
                i: sdata.end_date_time ? momentTimeFormater(sdata.end_date_time).format('YYYY-MM-DD HH:mm:ss') : '--',
                j: savings ? truncateNumber(savings.smartCost, 3) : null,
                k: savings ? truncateNumber(savings.hypotheticalCost, 3) : null,
                l: savings ? truncateNumber(savings.savings, 3) : null,
                m: savings ? truncateNumber(savings.savingsPercent, 3) : null,
            };
        });
        DownloadAsExcel(excelData, `Smart Charging sessions`, [
            'SESSION_ID',
            'TAG NAME',
            'EVSE ID',
            'CONNECTOR ID',
            'CONSUMPTION (kWh)',
            'STATUS',
            'LAST REPORTED SOC',
            'START TIME (CET)',
            'END TIME (CET)',
            'SMART COST*',
            'DEFAULT COST*',
            'SAVINGS*',
            'SAVINGS (%)*',
        ]);
    };

    const handlePageChange = (pageno) => {
        setSkip(LIMIT * pageno);
        setPage(pageno);
    };

    return (
        <div className={classes.Ventilation + ' ventilation'}>
            <div className={myClasses.FilterContainer}>
                <div className={myClasses.HeadingContainer}></div>

                <div className={myClasses.DatePickers}>
                    <div className={classes.FieldControl2}>
                        <label>Site</label>
                        <DropdownComponent name="groupId" options={groups} defaultValue={selectedGroup} onChange={(e) => setSelectedGroup(e)} />
                    </div>

                    <div className={classes.FieldControl2}>
                        <label>EVSE</label>
                        <DropdownComponent name="charger" options={chargers} defaultValue={selectedCharger} onChange={(e) => setSelectedCharger(e)} />
                    </div>

                    <div className={classes.FieldControl2}>
                        <label>Connector</label>
                        <DropdownComponent
                            name="connector"
                            options={connectors}
                            defaultValue={selectedConnector}
                            onChange={(e) => {
                                if (e.value !== selectedConnector.value) setSelectedConnector({ ...e, key: e.value });
                            }}
                        />
                    </div>

                    <div className={classes.FieldControl2}>
                        <label>Tag ID</label>
                        <DropdownComponent name="tags" options={tags} defaultValue={selectedTag} onChange={(e) => setSelectedTag(e)} />
                    </div>
                    <div className={classes.FieldControl2}>
                        <label>Status</label>
                        <DropdownComponent name="status" options={statusOptions} defaultValue={selectedStatus} onChange={(e) => setSelectedStatus(e)} />
                    </div>
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div className={classes.DateLabels}>Start Date</div>
                            <div className={classes.DateLabels} style={{ marginRight: '2.604vw' }}>
                                End Date
                            </div>
                        </div>
                        <DateRangePicker
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            onChange={(date) => {
                                setDateRange({ startDate: new Date(date.startDate), endDate: new Date(date.endDate) });
                            }}
                        />
                    </div>
                    <div
                        className={classes.SubmitButton}
                        onClick={() => {
                            fetchSessionData();
                            setPage(0);
                        }}
                    >
                        <button className="btn-primary" style={{ marginTop: '1.2vw' }}>
                            Submit
                        </button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <UilImport size="1.8vw" style={{ color: 'var(--color-primary)', cursor: 'pointer' }} onClick={() => fetchSessionData(true)} />
                    </div>
                </div>
            </div>

            <div className={classes.Alert}>
                <div className={myClasses.wholeFilterWrapper}>
                    <div className={myClasses.HeadingContainer}>
                        <Typography content={`Sessions`} />
                        <div className={classes.TableCount}>
                            Total Count :
                            <span>
                                <Typography size="14" content={totalCount} />
                            </span>
                        </div>
                        <div className={classes.TableCount}>
                            Total Consumption :
                            <span>
                                <Typography size="14" content={`${totalConsumption} kWh`} />
                            </span>
                        </div>
                        <div className={classes.TableCount}>
                            Total Savings :
                            <span>
                                <Typography size="14" content={`${totalSavings} kr.`} />
                            </span>
                        </div>
                    </div>
                </div>
                <Table
                    head={[
                        'SESSION ID',
                        'TAG NAME',
                        'TAG ID',
                        'CHARGER ID',
                        'CONNECTOR ID',
                        'CONSUMPTION (kWh)',
                        'STATUS',
                        'LAST REP. SOC',
                        'PROFILE STATUS',
                        'START TIME (CET)',
                        'END TIME (CET)',
                        'SAVINGS (DKK)*',
                        'Action',
                    ]}
                    keys={[
                        'sessionId',
                        'tagName',
                        'tagId',
                        'evseId',
                        'connectorId',
                        'consumption',
                        'status',
                        'lastReportedSOC',
                        'chargingProfileStatus',
                        'startTime',
                        'endTime',
                        'savings',
                        'action',
                    ]}
                    data={tableData}
                    page={page}
                    Pagination={true}
                    limit={LIMIT}
                    handlePageChange={handlePageChange}
                    totalCount={totalCount}
                />
            </div>

            <ModalComponent isOpen={infoModal.status} setOpen={() => setInfoModal({ status: false, data: '' })}>
                <SessionInfoModal
                    setOpen={() => setInfoModal({ status: false, data: '' })}
                    data={infoModal.data}
                    updateQueryParams={updateQueryParams}
                    queryParamsData={queryParamsData}
                />
            </ModalComponent>
            <ModalComponent
                isOpen={scInfoModal.status}
                setOpen={() =>
                    setSCInfoModal({
                        status: false,
                        sessionId: '',
                        evseId: '',
                    })
                }
            >
                <SmartChargeInfoModal
                    setOpen={() => setSCInfoModal({ status: false, sessionId: '', evseId: '' })}
                    data={scInfoModal.data}
                    sessionId={scInfoModal.sessionId}
                    evseId={scInfoModal.evseId}
                    fetchSessionData={fetchSessionData}
                />
            </ModalComponent>
            <ModalComponent
                isOpen={logsModal.status}
                setOpen={() =>
                    setLogsModal({
                        status: false,
                        sessionId: '',
                    })
                }
            >
                <SessionLogModal setOpen={() => setLogsModal({ status: false, sessionId: '' })} sessionId={logsModal.sessionId} />
            </ModalComponent>
        </div>
    );
};

export default Sessions;
